import { useMemo } from 'react';
import { useGetCustomersQuery, useGetPlantsQuery } from '../../gql/generated';
import {
  EDGE_DEVICE_CONNECTION_STATUS_OPTIONS,
  EDGE_DEVICE_STATUS_OPTIONS,
  useMachineFilters,
} from '../../hooks/useMachineFilters';
import { FilterTag } from './ActiveFilterTags';
import { STATUS_OPTIONS } from './StatusFilter';

/**
 * Hook that returns active filter tags for machine filters.
 * Provides tag objects compatible with the ActiveFilterTags component.
 */
export function useMachineFilterTags(): FilterTag[] {
  const {
    filters,
    setFilter,
    setDisplayName,
    setSerial,
    setPartNumber,
    setCustomers,
    setPlants,
    setFamilies,
    setStatuses,
    setCountryCodes,
    setCountries,
    setCities,
    setStates,
  } = useMachineFilters();

  // Fetch data for label lookups
  const { data: customersData } = useGetCustomersQuery({
    fetchPolicy: 'cache-and-network',
  });

  const { data: plantsData } = useGetPlantsQuery({
    fetchPolicy: 'cache-and-network',
  });

  // Transform to lookup maps for label display
  const customerOptions = useMemo(
    () =>
      (customersData?.customers || []).map((c) => ({
        value: c.id,
        label: c.displayName || c.id,
      })),
    [customersData]
  );

  const plantOptions = useMemo(
    () =>
      (plantsData?.plants || []).map((p) => ({
        value: p.id,
        label: p.displayName || p.id,
      })),
    [plantsData]
  );

  // Build list of active filters for the tag display
  const activeFilterTags = useMemo(() => {
    const tags: FilterTag[] = [];

    // Search filters
    if (filters.displayName) {
      tags.push({
        key: 'displayName',
        label: `Name: ${filters.displayName}`,
        onRemove: () => setDisplayName(''),
      });
    }
    if (filters.serial) {
      tags.push({
        key: 'serial',
        label: `Serial: ${filters.serial}`,
        onRemove: () => setSerial(''),
      });
    }
    if (filters.partNumber) {
      tags.push({
        key: 'partNumber',
        label: `Part #: ${filters.partNumber}`,
        onRemove: () => setPartNumber(''),
      });
    }

    // Multi-select filters - add each selected value as a tag
    filters.customers.forEach((id) => {
      const option = customerOptions.find((o) => o.value === id);
      tags.push({
        key: `customer-${id}`,
        label: option?.label || id,
        onRemove: () => setCustomers(filters.customers.filter((c) => c !== id)),
      });
    });

    filters.plants.forEach((id) => {
      const option = plantOptions.find((o) => o.value === id);
      tags.push({
        key: `plant-${id}`,
        label: option?.label || id,
        onRemove: () => setPlants(filters.plants.filter((p) => p !== id)),
      });
    });

    filters.families.forEach((value) => {
      tags.push({
        key: `family-${value}`,
        label: value,
        onRemove: () =>
          setFamilies(filters.families.filter((f) => f !== value)),
      });
    });

    filters.statuses.forEach((value) => {
      const option = STATUS_OPTIONS.find((o) => o.value === value);
      tags.push({
        key: `status-${value}`,
        label: option?.label || value,
        onRemove: () =>
          setStatuses(filters.statuses.filter((s) => s !== value)),
      });
    });

    filters.countryCodes.forEach((value) => {
      tags.push({
        key: `countryCode-${value}`,
        label: value,
        onRemove: () =>
          setCountryCodes(filters.countryCodes.filter((c) => c !== value)),
      });
    });

    filters.countries.forEach((value) => {
      tags.push({
        key: `country-${value}`,
        label: value,
        onRemove: () =>
          setCountries(filters.countries.filter((c) => c !== value)),
      });
    });

    filters.cities.forEach((value) => {
      tags.push({
        key: `city-${value}`,
        label: value,
        onRemove: () => setCities(filters.cities.filter((c) => c !== value)),
      });
    });

    filters.states.forEach((value) => {
      tags.push({
        key: `state-${value}`,
        label: value,
        onRemove: () => setStates(filters.states.filter((s) => s !== value)),
      });
    });

    filters.edgeDeviceStatuses.forEach((value) => {
      const option = EDGE_DEVICE_STATUS_OPTIONS.find((o) => o.value === value);
      tags.push({
        key: `edgeDeviceStatus-${value}`,
        label: option?.label || value,
        onRemove: () =>
          setFilter(
            'edgeDeviceStatuses',
            filters.edgeDeviceStatuses.filter((s) => s !== value)
          ),
      });
    });

    filters.edgeDeviceConnectionStatuses.forEach((value) => {
      const option = EDGE_DEVICE_CONNECTION_STATUS_OPTIONS.find(
        (o) => o.value === value
      );
      tags.push({
        key: `edgeDeviceConnectionStatus-${value}`,
        label: option?.label || value,
        onRemove: () =>
          setFilter(
            'edgeDeviceConnectionStatuses',
            filters.edgeDeviceConnectionStatuses.filter((s) => s !== value)
          ),
      });
    });

    return tags;
  }, [
    filters,
    setFilter,
    customerOptions,
    plantOptions,
    setDisplayName,
    setSerial,
    setPartNumber,
    setCustomers,
    setPlants,
    setFamilies,
    setStatuses,
    setCountryCodes,
    setCountries,
    setCities,
    setStates,
  ]);

  return activeFilterTags;
}
